import { ListPagination } from "Common/ListPagination";
import ParentList from "Common/Modals/ParentList";
import TableContainer from "Common/TableContainer";
import TableEntry from "Common/TableEntry";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { currentDateTime, getStartTimeOfDay } from "Utils/datefilter";

import { useEffect, useMemo, useRef, useState } from "react";
import { authServices } from "Utils/auth/services";

interface ParentLevl {
  user_id: string;
  user_name: string;
  name: string;
  user_type_id: number;
  domain_name: string;
  parent_id: string;
  parent_user_name: string;
  accessToken: string;
  login_status: string;
  is_online: number;
  message: string;
  ip_address: string;
  browser_info: string;
  parent_level_ids: ParentLevelId[];
  expireAt: string;
  login_time: string;
  logout_time: string;
  geolocation?: any;
}

interface ParentLevelId {
  user_id: string;
  user_name: string;
  user_type_id: number;
}

interface ProfitLossTotal {
  total: number;
  page: number;
}

interface Props {
  activeTab: string;
  username: any ;
  filter?: boolean;
}

const LoginHistory = ({ activeTab, username, filter }: Props) => {
  const today = new Date();
  const initialStartDate = getStartTimeOfDay(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
  const initialEndDate = currentDateTime();

  const [activeLog, setActiveLog] = useState<ParentLevl[]>([]);
  const [limit, setLimit] = useState<number>(50);
  const [page, setPage] = useState<number>(1);
  const [userName, setUserName] = useState<string>("");
  const [userIds, setUserids] = useState<string>("");
  const [showParentList, setShowParentList] = useState<boolean>(false);
  const [fixedCountryData, setFixedCountryData] = useState<any>(null);
  const [startDate, setStartDate] = useState<Date | string>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | string>(initialEndDate);
  const [listTotal, setListTotal] = useState<ProfitLossTotal>({ total: 0, page: 1 });

  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const getActiveLogs = async (filters: any = {}) => {
    const { response } = await authServices.getActiveLog({
      limit: filters.limit || limit,
      page: filters.page || page,
      search: {
        user_names: [username],
      },
      from_date: filters.startDate || startDate,
      to_date: filters.endDate || endDate,
    });

    if (response?.data) {
      setActiveLog(response.data);
      setListTotal({
        total: response.metadata?.total || 0,
        page: filters.page || page,
      });
    } else {
      setActiveLog([]);
      setListTotal({ total: 0, page: 1 });
    }
  };

  useEffect(() => {
    if (activeTab === "LoginHistory") {
      getActiveLogs({ limit, page });
    }
  }, [page, limit, username, activeTab]);

  const handlePageFilter = (page: number, limit: number) => {
    setPage(page);
    setLimit(limit);
  };

  const handleFilterSubmit = () => {
    setPage(1);
    getActiveLogs({ limit, page: 1, startDate, endDate });
  };

  const handleClearSelection = () => {
    const resetStart = getStartTimeOfDay(new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000));
    const resetEnd = currentDateTime();
    setStartDate(resetStart);
    setEndDate(resetEnd);
    setPage(1);
    getActiveLogs({ limit, page: 1, startDate: resetStart, endDate: resetEnd });
  };

  const handleParentUserList = (user_id: string, name: string) => {
    setUserName(name);
    setUserids(user_id);
    setShowParentList(true);
  };

  const handleCloseParentUserList = () => {
    setShowParentList(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setFixedCountryData(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "No.",
        accessorKey: "no",
        cell: (cell: any) => cell.row.index + 1,
      },
      {
        header: "User Name",
        accessorKey: "user_name",
        cell: (cell: any) => {
          const row = cell?.row?.original;
          return (
            <div className="cursor-pointer" onClick={() => handleParentUserList(row?.user_id, row?.user_name)}>
              {row?.user_name}
            </div>
          );
        },
      },
      {
        header: "Login Time",
        accessorKey: "login_time",
        cell: (cell: any) => {
          const row = cell?.row?.original;
          return (
            <div>
              <p className="m-0">{moment(row?.login_time).format("DD-MM-YYYY")}</p>
              <p className="m-0">{moment(row?.login_time).format("hh:mm:ss:SSS A")}</p>
            </div>
          );
        },
      },
      {
        header: "Status",
        accessorKey: "login_status",
        cell: (cell: any) => {
          const row = cell?.row?.original;
          return (
            <div className={row.login_status?.toLowerCase() === "login_success" ? "text-success" : "text-danger"}>
              {row?.message}
            </div>
          );
        },
      },
      {
        header: "IP Address",
        accessorKey: "ip_address",
        cell: (cell: any) => {
          const row = cell?.row?.original;
          const geoData = typeof row?.geolocation === "string" ? JSON.parse(row.geolocation || "{}") : row?.geolocation;

          const handleMouseEnter = () => {
            if (geoData) {
              setFixedCountryData(geoData);
            }
          };

          const handleMouseLeave = () => {
            setFixedCountryData(null);
          };

          return (
            <div className="position-relative">
              <span className="text-info">
                <i
                  className="bi bi-globe me-1 cursor-pointer"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  style={{ verticalAlign: "middle" }}
                ></i>
                {row?.ip_address}
              </span>
              {fixedCountryData && (
                <div
                  ref={tooltipRef}
                  className="bg-white p-3 rounded position-absolute"
                  style={{
                    left: "0px",
                    minWidth: "200px",
                    zIndex: 999,
                    boxShadow: "0px 8px 16px rgba(0,0,0,0.2)",
                  }}
                >
                  <div className="d-flex justify-content-between mb-1">
                    <p className="fw-bold m-0">City:</p>
                    <p className="m-0">{fixedCountryData?.city}</p>
                  </div>
                  <div className="d-flex justify-content-between mb-1">
                    <p className="fw-bold m-0">State:</p>
                    <p className="m-0">{fixedCountryData?.state}</p>
                  </div>
                  <div className="d-flex justify-content-between">
                    <p className="fw-bold m-0">Country:</p>
                    <p className="m-0">{fixedCountryData?.country}</p>
                  </div>
                </div>
              )}
            </div>
          );
        },
      },
      {
        header: "Browser Info",
        accessorKey: "browser_info",
      },
    ],
    [fixedCountryData]
  );

  return (
    <div>
      {filter && (
        <Card>
          <Card.Body>
            <Form>
              <Row className="gy-3 align-items-end">
                <Col lg={9}>
                  <Row className="gap-3 gap-sm-0">
                    <Col lg={4}>
                      <Form.Label>Start Date</Form.Label>
                      <Flatpickr
                        className="form-control"
                        options={{ enableTime: true, dateFormat: "Y-m-d H:i:S", maxDate: endDate }}
                        value={startDate}
                        onChange={([date]: [Date]) => setStartDate(date)}
                      />
                    </Col>
                    <Col lg={4}>
                      <Form.Label>End Date</Form.Label>
                      <Flatpickr
                        className="form-control"
                        options={{ enableTime: true, dateFormat: "Y-m-d H:i:S", maxDate: currentDateTime() }}
                        value={endDate}
                        onChange={([date]: [Date]) => setEndDate(date)}
                      />
                    </Col>
                  </Row>
                </Col>
                <Col lg={3}>
                  <Button className="me-2 btn-sm" style={{ height: "38px" }} onClick={handleFilterSubmit}>
                    <i className="fs-lg align-middle ri-filter-line"></i> Submit
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="btn-sm"
                    style={{ height: "38px" }}
                    onClick={handleClearSelection}
                  >
                    <i className="fs-lg align-middle ri-refresh-line"></i> Clear
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body>
          <TableContainer
            columns={columns}
            data={activeLog}
            isBordered={false}
            customPageSize={50}
            isPagination={false}
            sorting={true}
            tableClass="table-centered align-middle table-nowrap table-striped mb-3"
            theadClass="text-muted table-light"
            SearchPlaceholder="Search Login History..."
          />
          <nav className="px-3 d-flex flex-column flex-sm-row align-items-center">
            <TableEntry limit={limit} handlelimitFilter={(value: number) => handlePageFilter(1, value)} />
            <ListPagination listTotal={listTotal} handlePageFilter={(value: number) => handlePageFilter(value, limit)} />
          </nav>
        </Card.Body>
      </Card>

      <ParentList userName={userName} userIds={userIds} show={showParentList} clickHandler={handleCloseParentUserList} />
    </div>
  );
};

export default LoginHistory;
